import React, { useEffect, useMemo, useState } from "react";
import { Alert, Grid2, Typography } from "@mui/material";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useTranslate } from "../../../../hooks/useTranslate";
import { useStudents } from "../../../../context/students";
import SummaryBox from "../../../../components/SummaryBox";
import Preloader from "../../../../components/Preloader";
import NextPaymentAmountIco from "../../../../assets/images/icons/next-payment-amount.svg?react";
import NextPaymentDateIco from "../../../../assets/images/icons/next-payment-date.svg?react";
import FinalPaymentDateIco from "../../../../assets/images/icons/final-payment-date.svg?react";
import BillingFrequencyIco from "../../../../assets/images/icons/billing-frequency.svg?react";
import IconCard from "../../../../components/IconCard";
import BillingTable from "../components/BillingTable";
import { useGetBillingSummary } from "../../../../hooks/useGetBillingSummary";
import { formatTo, isDateInPast } from "../../../../utils/dateTimeHelper";
import EmptyContent from "../../../../components/EmptyContent";
import { useGetContractsSummary } from "../../../../hooks/useGetContractsSummary";
import { hasPermission, PERMISSIONS } from "../../../../constants/permissions";
import LockedPage from "../../../../components/LockedPage";
import TaskCard from "../../../../components/TaskCard";
import { usePaymentMethods } from "../../../../hooks/usePaymentMethods";
import BaseDialog from "../../../../components/BaseDialog";
import DefaultPaymentForm from "../components/DefaultPaymentForm";
import "./Payments.css";
import { useSearchParams } from "react-router-dom";
import cn from "classnames";
import { useGetStudentContacts } from "../../../../hooks/useGetStudentContacts";
import { useAuth } from "../../../../auth";
import { useFeatures } from "../../../../context/features";

export const annualMonthly = "Annual (Paid monthly)";
export const annualFull = "Annual (Paid in full)";
export const rollingContract = "Rolling Contract";

export const Payments = () => {
    const { t } = useTranslate();
    const { userId } = useAuth();
    const { isFeatureEnabled } = useFeatures();
    const { activeStudentId, permissions } = useStudents();
    const [searchParams] = useSearchParams();
    const hasPaymentMethodComplete = searchParams.has("payment-method-complete");

    const { isLoading: isLoadingContact, contacts } = useGetStudentContacts(activeStudentId);
    const contact = useMemo(() => contacts.find((contact) => contact.contactId === userId) || {}, [contacts, userId]);
    const isPrimaryContact = contact?.isPrimary;

    const showTuitionBillingInfo = isFeatureEnabled("parent.tuitionbillinginformation.enabled");
    const canFinanceView = hasPermission(permissions, PERMISSIONS.FINANCE_MANAGE);
    const canFinanceManage = hasPermission(permissions, PERMISSIONS.FINANCE_MANAGE);

    const { isLoading: isLoadingContractsSummary, contractsSummary } = useGetContractsSummary(activeStudentId);
    const { isLoading, billingSummary } = useGetBillingSummary(activeStudentId);
    const { isLoading: isLoadingPaymentMethods, paymentMethods, refetch: refetchPaymentMethods } = usePaymentMethods(isPrimaryContact ? activeStudentId : null);

    const [showManageModal, setShowManageModal] = useState(false);
    const [showSuccessAccessMessage, setShowSuccessAccessMessage] = useState(false);

    const hasOverduePayments = (billingSummary?.upcomingInvoices || []).some((item) => item.status.toLowerCase().includes("due"));

    const billingFrequency = useMemo(() => {
        if (!contractsSummary || !billingSummary) return "";

        const { contractType } = contractsSummary;
        const { paymentSchedule } = billingSummary;

        if (contractType === "Annual") {
            if (paymentSchedule === "Full") return annualFull;
            if (["Monthly", "FrcMonthly"].includes(paymentSchedule)) return annualMonthly;
        }

        if (contractType === "Rolling" && ["Full", "FrcMonthly"].includes(paymentSchedule)) {
            return rollingContract;
        }

        return "";
    }, [contractsSummary, billingSummary]);
    const { nextPaymentDate, nextPaymentAmount, finalPaymentDate } = useMemo(() => {
        if (!contractsSummary || !billingSummary) {
            return { nextPaymentDate: "", nextPaymentAmount: "", finalPaymentDate: "" };
        }

        const { contractType } = contractsSummary;
        const {
            paymentSchedule: schedule, nextPaymentDate, nextPaymentAmount, finalPaymentDate,
        } = billingSummary;

        if (contractType === "Annual" && schedule.includes("Monthly")) {
            return { nextPaymentDate, nextPaymentAmount, finalPaymentDate };
        }

        return { nextPaymentDate: "", nextPaymentAmount: "", finalPaymentDate: "" };
    }, [contractsSummary, billingSummary]);

    const showAdditionalCards = billingFrequency === annualMonthly;

    const billingFrequencyBox = useMemo(() => {
        if (!billingFrequency) return null;
        return (
            <Grid2 size={{ xs: 10, sm: 10, lg: 3 }}>
                <IconCard
                    icon={<BillingFrequencyIco />}
                    title={t("payments.frequency")}
                >
                    <Typography variant="body1">{billingFrequency}</Typography>
                </IconCard>
            </Grid2>
        )
    }, [billingFrequency, t]);
    const nextPaymentDateBox = useMemo(() => (
        <Grid2 size={{ xs: 10, sm: 10, lg: 3 }}>
            <IconCard
                icon={<NextPaymentDateIco />}
                title={t("payments.nextPaymentDate")}
            >
                <Typography variant="body1">
                    {nextPaymentDate && !isDateInPast(nextPaymentDate) ? formatTo(nextPaymentDate, "dd/MM/yyyy") : "-"}
                </Typography>
            </IconCard>
        </Grid2>
    ), [nextPaymentDate, t]);
    const nextPaymentAmountBox = useMemo(() => {
        if (!nextPaymentAmount) return null;
        return (
            <Grid2 size={{ xs: 10, sm: 10, lg: 3 }}>
                <IconCard
                    icon={<NextPaymentAmountIco />}
                    title={t("payments.nextPaymentAmount")}
                >
                    <Typography variant="body1">
                        {!isDateInPast(nextPaymentDate) ? `£${nextPaymentAmount}` : "-"}
                    </Typography>
                </IconCard>
            </Grid2>
        )
    }, [nextPaymentAmount, nextPaymentDate, t]);
    const finalPaymentDateBox = useMemo(() => (
        <Grid2 size={{ xs: 10, sm: 10, lg: 3 }}>
            <IconCard
                icon={<FinalPaymentDateIco />}
                title={t("payments.finalPaymentDate")}
            >
                <Typography variant="body1">
                    {finalPaymentDate ? formatTo(finalPaymentDate, "dd/MM/yyyy") : "-"}
                </Typography>
            </IconCard>
        </Grid2>
    ), [finalPaymentDate, t]);

    useEffect(() => {
        if (hasPaymentMethodComplete) {
            setShowSuccessAccessMessage(true);

            setTimeout(() => {
                setShowSuccessAccessMessage(false);
            }, 10000);
        }
    }, [hasPaymentMethodComplete]);

    return (
        <Preloader isLoading={isLoadingPaymentMethods || isLoadingContact} overflow showInitiallyOverflow>
            <div className="page-content">
                <div className="student-head-section" />
                <Grid2
                    container
                    spacing={2}
                    className={canFinanceView ? "min-head-section-height--small" : "min-head-section-height"}
                >
                    <Grid2 size={{ xs: 12, md: 7, lg: 8 }} className="text-white">
                        <Breadcrumbs
                            breadcrumbs={[
                                { label: t("navigation.home"), href: `/home/${activeStudentId}` },
                                { label: t("finance.title"), href: `/finance/${activeStudentId}` },
                                { label: t("payments.title"), current: true },
                            ]}
                        />
                        <Typography variant="h1" className="text-white">
                            {t("payments.title")}
                        </Typography>
                        <Typography variant="body1" className="mt-4 text-white" component="div">
                            {t("payments.subtitle")}
                            <a
                                className="text-white"
                                href={`mailto:${t("finance.emailAccounts")}`}
                            >
                                {t("finance.emailAccounts")}
                            </a>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                        {(isPrimaryContact) && (
                            <TaskCard
                                className="payments__task-card"
                                title={t("payments.managePaymentDetails")}
                                onTitleClick={() => setShowManageModal(true)}
                                showArrow
                            >
                                <Preloader isLoading={isLoadingPaymentMethods} size={20}>
                                    <div className="w-full">
                                        {paymentMethods?.[0] ? (
                                            <Typography variant="h5" component="div">
                                                {t("payments.viewAndUpdateSavedMethod")}
                                            </Typography>
                                        ) : (
                                            <Typography variant="h5" component="div" className="text-error">
                                                {t("payments.noSavedMethod")}
                                            </Typography>
                                        )}
                                    </div>
                                </Preloader>
                            </TaskCard>
                        )}
                    </Grid2>
                    {/* {hasOverduePayments && (
                    <Alert
                        variant="filled"
                        severity="warning"
                        className="mt-8 w-full text-white"
                        icon={<IconInfo className="alert-icon" />}
                        action={(
                            <Button
                                onClick={() => window.location.reload()}
                                className="button-accent alert-button"
                            >
                                {t("payments.update")}
                            </Button>
                        )}
                    >
                        {t("payments.overdueAlert")}
                    </Alert>
                )} */}
                </Grid2>
                <div className="relative z-1">
                    <LockedPage showPermissionText isLocked={!canFinanceView}>
                        <Preloader isLoading={isLoading || isLoadingContractsSummary}>
                            {showSuccessAccessMessage && (
                                <Typography variant="body1" component="div" className="message-success text-white">
                                    {t("payments.methodUpdatedSuccess")}
                                </Typography>
                            )}
                            {showTuitionBillingInfo && (
                                <>
                                    <Typography variant="h3" className={cn("text-white", showSuccessAccessMessage ? "mt-8" : "")}>
                                        {t("payments.tuitionBillingInformation")}
                                    </Typography>
                                    {(billingFrequency || showAdditionalCards) && (
                                        <Grid2 container spacing={3} className="mt-4">
                                            {billingFrequencyBox}
                                            {showAdditionalCards && (
                                                <>
                                                    {nextPaymentDateBox}
                                                    {nextPaymentAmountBox}
                                                    {finalPaymentDateBox}
                                                </>
                                            )}
                                        </Grid2>
                                    )}
                                </>
                            )}
                            <SummaryBox isFormBox className="bg-greyish mt-8">
                                <Typography variant="h4" component="div" className="text-primary">
                                    {t("payments.upcomingOutstandingInvoices")}
                                </Typography>
                                <EmptyContent
                                    data={billingSummary?.upcomingInvoices}
                                    text={(
                                        <Typography type="body1" component="div" className="transform-none">
                                            {t("payments.upcomingOutstandingInvoicesEmptyText")}
                                        </Typography>
                                    )}
                                    className="mt-4"
                                >
                                    <BillingTable
                                        className="mt-4"
                                        activeStudentId={activeStudentId}
                                        data={billingSummary?.upcomingInvoices || []}
                                        showPay={canFinanceManage}
                                    />
                                </EmptyContent>
                            </SummaryBox>
                            <SummaryBox isFormBox className="bg-greyish mt-8">
                                <Typography variant="h4" component="div" className="text-primary">
                                    {t("payments.billingHistory")}
                                </Typography>
                                <EmptyContent data={billingSummary?.billingHistory} text={t("common.noDataText")} className="mt-4">
                                    <BillingTable
                                        className="mt-4"
                                        activeStudentId={activeStudentId}
                                        data={billingSummary?.billingHistory || []}
                                        isHistory
                                    />
                                </EmptyContent>
                            </SummaryBox>
                        </Preloader>
                    </LockedPage>
                </div>
                {/* <SnackbarToast */}
                {/*    open={!!errorSetPaymentPlan || !!errorConfigure} */}
                {/*    isError={!!errorSetPaymentPlan || !!errorConfigure} */}
                {/*    onClose={() => { */}
                {/*        resetPaymentPlan(); */}
                {/*        resetConfigure(); */}
                {/*    }} */}
                {/* > */}
                {/*    {`${t("tuitionBilling.errorProceedMessage")} (status ${ */}
                {/*        (errorSetPaymentPlan || errorConfigure)?.response?.status */}
                {/*    })`} */}
                {/* </SnackbarToast> */}
            </div>
            <DefaultPaymentForm
                open={showManageModal}
                onClose={() => setShowManageModal(false)}
                isLoading={isLoadingPaymentMethods}
                defaultPayment={paymentMethods?.[0]}
                paymentMethods={paymentMethods}
                refetch={refetchPaymentMethods}
            />
        </Preloader>
    );
};
